import React, { useState } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Modal, Button, ButtonToolbar, Alert, Spinner, Card, Row, Col, Table, Form } from 'react-bootstrap';
import * as Icon from 'react-feather';

const CANCEL_REASONS = [
    { value: '01', label: '01 - Comprobante emitido con errores con relacion' },
    { value: '02', label: '02 - Comprobante emitido con errores sin relacion' },
    { value: '03', label: '03 - No se llevo a cabo la operacion' },
    { value: '04', label: '04 - Operacion nominativa relacionada con una factura global' },
];

function FacturaModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [cancelReason, setCancelReason] = useState('03');
    const [uuidSustitucion, setUuidSustitucion] = useState('');
    const [observations, setObservations] = useState('');

    const tripId = props.tripId;
    const maneuverId = props.maneuverId;
    const stayId = props.stayId;

    // Build API paths based on whether this is a trip or maneuver invoice
    const statusUrl = stayId
        ? `${apiUrl}/cartaporte/factura/stay/status/${tripId}/${stayId}`
        : maneuverId
        ? `${apiUrl}/cartaporte/factura/maneuver/status/${tripId}/${maneuverId}`
        : `${apiUrl}/cartaporte/factura/status/${tripId}`;
    const previewUrl = stayId
        ? `${apiUrl}/cartaporte/factura/stay/preview/${tripId}/${stayId}`
        : maneuverId
        ? `${apiUrl}/cartaporte/factura/maneuver/preview/${tripId}/${maneuverId}`
        : `${apiUrl}/cartaporte/factura/preview/${tripId}`;
    const generateUrl = stayId
        ? `${apiUrl}/cartaporte/factura/stay/generate/${tripId}/${stayId}`
        : maneuverId
        ? `${apiUrl}/cartaporte/factura/maneuver/generate/${tripId}/${maneuverId}`
        : `${apiUrl}/cartaporte/factura/generate/${tripId}`;
    const modalTitle = stayId
        ? `Factura Estadia - Viaje #${tripId}`
        : maneuverId
        ? `Factura Maniobra - Viaje #${tripId}`
        : `Factura Ingreso - Viaje #${tripId}`;

    const onModalOpen = () => {
        setError(null);
        setSuccess(null);
        setPreview(null);
        setObservations(props.defaultObservations || '');
        resetCancelForm();
        loadData();
    };

    const resetCancelForm = () => {
        setShowCancelConfirm(false);
        setCancelReason('03');
        setUuidSustitucion('');
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const statusRes = await axios.get(statusUrl, {
                headers: { Authorization: authHeader() }
            });
            const statusData = statusRes.data.msg;
            setStatus(statusData);

            if (
                statusData.can_generate !== false &&
                (!statusData.exists || statusData.status === 'ERROR' || statusData.status === 'CANCELLED')
            ) {
                const previewRes = await axios.get(previewUrl, {
                    headers: { Authorization: authHeader() }
                });
                setPreview(previewRes.data.msg);
            }
        } catch (err) {
            setError('Error al cargar estado de factura');
            console.error(err);
        }
        setLoading(false);
    };

    const handleGenerate = async () => {
        if (status?.can_generate === false) {
            setError('Este viaje ya tiene una factura registrada.');
            return;
        }

        setGenerating(true);
        setError(null);
        try {
            const res = await axios.post(
                generateUrl,
                { observations: observations.trim() },
                { headers: { Authorization: authHeader() } }
            );
            setSuccess(`Factura generada exitosamente. UUID: ${res.data.uuid}`);
            loadData();
            if (props.onGenerated) props.onGenerated();
        } catch (err) {
            const errData = err.response?.data;
            const errMsg = errData?.details || errData?.error || 'Error al generar factura';
            setError(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
        }
        setGenerating(false);
    };

    const handleDownload = async (fileType) => {
        if (!status || !status.id) return;
        try {
            const res = await axios.get(
                `${apiUrl}/cartaporte/factura/download/${status.id}/${fileType}`,
                { headers: { Authorization: authHeader() } }
            );
            const fileData = res.data.msg;
            const byteCharacters = atob(fileData.file);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const mimeType = fileType === 'pdf' ? 'application/pdf' : 'application/xml';
            const blob = new Blob([byteArray], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileData.filename;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(`Error al descargar ${fileType.toUpperCase()}`);
        }
    };

    const handleCancel = async () => {
        if (!status || !status.id) return;

        if (cancelReason === '01' && !uuidSustitucion.trim()) {
            setError('Captura el UUID de sustitucion para el motivo 01.');
            return;
        }

        setCanceling(true);
        setError(null);
        try {
            await axios.post(
                `${apiUrl}/cartaporte/factura/cancel/${status.id}`,
                {
                    motivo: cancelReason,
                    uuid_sustitucion: uuidSustitucion.trim() || null,
                },
                { headers: { Authorization: authHeader() } }
            );
            setSuccess('Factura cancelada exitosamente.');
            resetCancelForm();
            await loadData();
            if (props.onGenerated) props.onGenerated();
        } catch (err) {
            const errData = err.response?.data;
            const errMsg = errData?.details || errData?.error || 'Error al cancelar factura';
            setError(typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg);
        } finally {
            setCanceling(false);
        }
    };

    const renderCancelPanel = () => showCancelConfirm && (
        <Alert variant="warning" className="mt-3 mb-0">
            <Alert.Heading className="h5">Cancelar factura</Alert.Heading>
            <p className="mb-3">Esta seguro que desea cancelar la factura?</p>
            <Form.Group className="mb-3">
                <Form.Label>Motivo de cancelacion</Form.Label>
                <Form.Select
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                >
                    {CANCEL_REASONS.map((reason) => (
                        <option key={reason.value} value={reason.value}>
                            {reason.label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>
            {cancelReason === '01' && (
                <Form.Group className="mb-3">
                    <Form.Label>UUID de sustitucion</Form.Label>
                    <Form.Control
                        value={uuidSustitucion}
                        onChange={(e) => setUuidSustitucion(e.target.value)}
                        placeholder="UUID del CFDI que sustituye"
                    />
                </Form.Group>
            )}
            <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={() => setShowCancelConfirm(false)} disabled={canceling}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={handleCancel}
                    disabled={canceling || (cancelReason === '01' && !uuidSustitucion.trim())}
                >
                    {canceling ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Cancelando...
                        </>
                    ) : (
                        'Si'
                    )}
                </Button>
            </div>
        </Alert>
    );

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return `$${parseFloat(amount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const renderStampedView = () => (
        <div>
            <Alert variant="success">
                <Alert.Heading>Factura Timbrada</Alert.Heading>
                <p><strong>UUID:</strong> {status.uuid}</p>
                <p><strong>Folio:</strong> {status.folio || 'N/A'}</p>
                <p><strong>Fecha de Timbrado:</strong> {status.fecha_timbrado || 'N/A'}</p>
                <Row className="mt-3">
                    <Col md={3}><strong>Subtotal:</strong> {formatCurrency(status.subtotal)}</Col>
                    <Col md={3}><strong>IVA Trasladado:</strong> {formatCurrency(status.iva_trasladado)}</Col>
                    <Col md={3}><strong>IVA Retenido:</strong> {formatCurrency(status.iva_retenido)}</Col>
                    <Col md={3}><strong>Total:</strong> {formatCurrency(status.total)}</Col>
                </Row>
            </Alert>
            <div className="d-flex gap-2">
                <Button variant="outline-primary" onClick={() => handleDownload('xml')}>
                    <Icon.Download size={14} /> Descargar XML
                </Button>
                <Button variant="outline-danger" onClick={() => handleDownload('pdf')}>
                    <Icon.Download size={14} /> Descargar PDF
                </Button>
                <Button variant="danger" onClick={() => setShowCancelConfirm(true)}>
                    <Icon.XCircle size={14} /> Cancelar
                </Button>
            </div>
            {renderCancelPanel()}
        </div>
    );

    const renderInvoiceLockedView = () => (
        <Alert variant="info">
            <Alert.Heading>Factura registrada</Alert.Heading>
            <p className="mb-0">
                Este viaje ya tiene la factura {status.invoice_id} registrada. No se puede generar otra factura para el mismo viaje.
            </p>
        </Alert>
    );

    const renderPreviewView = () => {
        if (!preview) return null;
        const { validation, emisor, receptor, concepto } = preview;

        return (
            <div>
                {validation && !validation.valid && (
                    <Alert variant="danger">
                        <Alert.Heading>Campos Faltantes</Alert.Heading>
                        <ul className="mb-0">
                            {validation.errors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {validation && validation.warnings && validation.warnings.length > 0 && (
                    <Alert variant="warning">
                        <Alert.Heading>Advertencias</Alert.Heading>
                        <ul className="mb-0">
                            {validation.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Emisor */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Emisor</h6></Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}><strong>RFC:</strong> {emisor.rfc || '-'}</Col>
                            <Col md={3}><strong>Nombre:</strong> {emisor.nombre || '-'}</Col>
                            <Col md={3}><strong>Regimen:</strong> {emisor.regimen_fiscal || '-'}</Col>
                            <Col md={3}><strong>C.P.:</strong> {emisor.codigo_postal || '-'}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Receptor */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Receptor (Cliente)</h6></Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}><strong>RFC:</strong> {receptor.rfc || '-'}</Col>
                            <Col md={3}><strong>Nombre:</strong> {receptor.nombre || '-'}</Col>
                            <Col md={3}><strong>Regimen:</strong> {receptor.regimen_fiscal || '-'}</Col>
                            <Col md={3}><strong>Uso CFDI:</strong> {receptor.uso_cfdi || '-'}</Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Concepto */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Concepto</h6></Card.Header>
                    <Card.Body>
                        <Table size="sm" bordered>
                            <thead>
                                <tr>
                                    <th>Cantidad</th>
                                    <th>Clave</th>
                                    <th>Unidad</th>
                                    <th>Descripcion</th>
                                    <th>Precio Unitario</th>
                                    <th>Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{concepto.cantidad}</td>
                                    <td>{concepto.clave_prod_serv}</td>
                                    <td>{concepto.clave_unidad}</td>
                                    <td>{concepto.descripcion}</td>
                                    <td>{formatCurrency(concepto.precio_unitario)}</td>
                                    <td>{formatCurrency(concepto.subtotal)}</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* Impuestos & Total */}
                <Card className="mb-3">
                    <Card.Header><h6 className="mb-0">Impuestos</h6></Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}>
                                <strong>Subtotal:</strong>
                                <div className="fs-5">{formatCurrency(concepto.subtotal)}</div>
                            </Col>
                            <Col md={3}>
                                <strong>IVA Trasladado (16%):</strong>
                                <div className="fs-5 text-danger">+{formatCurrency(concepto.iva_trasladado)}</div>
                            </Col>
                            <Col md={3}>
                                <strong>IVA Retenido (4%):</strong>
                                <div className="fs-5 text-success">-{formatCurrency(concepto.iva_retenido)}</div>
                            </Col>
                            <Col md={3}>
                                <strong>Total:</strong>
                                <div className="fs-4 fw-bold">{formatCurrency(concepto.total)}</div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {!props.hideObservationsInput && (
                    <Card className="mb-3">
                        <Card.Header><h6 className="mb-0">Observaciones</h6></Card.Header>
                        <Card.Body>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={observations}
                                onChange={(e) => setObservations(e.target.value)}
                                placeholder="Observaciones que apareceran en el PDF de la factura"
                                disabled={generating}
                            />
                        </Card.Body>
                    </Card>
                )}

                {/* Generate button */}
                {validation && validation.valid && (
                    <div className="text-end">
                        <Button
                            variant="success"
                            onClick={handleGenerate}
                            disabled={generating || status?.can_generate === false}
                        >
                            {generating ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Generando...
                                </>
                            ) : (
                                <>
                                    <Icon.DollarSign size={14} className="me-1" />
                                    Generar Factura
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            show={props.show}
            onHide={props.onHide}
            onShow={onModalOpen}
            size="xl"
            aria-labelledby="factura-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title id="factura-modal">
                    {modalTitle}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Cargando...</p>
                    </div>
                ) : (
                    <>
                        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

                        {status && status.exists && status.status === 'STAMPED' && renderStampedView()}
                        {status && status.can_generate === false && status.status !== 'STAMPED' && renderInvoiceLockedView()}
                        {status && status.exists && status.status === 'ERROR' && (
                            <>
                                <Alert variant="danger">
                                    <Alert.Heading>Error en Generacion Anterior</Alert.Heading>
                                    <p>{status.error_message || 'Facturama no regreso detalle del error'}</p>
                                    {status.error_code && (
                                        <p className="mb-0"><strong>Codigo Facturama:</strong> {status.error_code}</p>
                                    )}
                                </Alert>
                                {status.can_generate !== false && renderPreviewView()}
                            </>
                        )}
                        {status && status.exists && status.status === 'CANCELLED' && (
                            <>
                                <Alert variant="secondary">
                                    Esta factura fue cancelada{status.cancel_date ? ` el ${status.cancel_date}` : ''}.
                                    {status.cancel_reason && (
                                        <div><strong>Motivo:</strong> {status.cancel_reason}</div>
                                    )}
                                </Alert>
                                {status.can_generate !== false && renderPreviewView()}
                            </>
                        )}
                        {(!status || (!status.exists && status.can_generate !== false)) && renderPreviewView()}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}


function FacturaButton({ tripId, maneuverId, stayId, defaultObservations, hideObservationsInput, setData }) {
    const [modalShow, setModalShow] = useState(false);

    const handleClose = () => {
        if (setData) setData();
        setModalShow(false);
    };

    return (
        <ButtonToolbar>
            <Button
                size="sm"
                variant="outline-secondary"
                style={{ padding: '0 0 0 0' }}
                onClick={() => setModalShow(true)}
                title="Factura Ingreso"
            >
                <Icon.DollarSign size={16} />
            </Button>
            <FacturaModal
                show={modalShow}
                onHide={handleClose}
                tripId={tripId}
                maneuverId={maneuverId}
                stayId={stayId}
                defaultObservations={defaultObservations}
                hideObservationsInput={hideObservationsInput}
                onGenerated={setData}
            />
        </ButtonToolbar>
    );
}

export default FacturaButton;
